import React from "react";
import { format } from "date-fns";
import {
  Building2,
  ChevronDown,
  CreditCard,
  FileText,
  RotateCcw,
  Ticket,
  Users,
  MoreHorizontal,
  Download,
  AlertTriangle,
  Loader2,
  Info,
  Clock,
  Check,
  X,
  DollarSign,
  Receipt,
  AlertCircle,
  History,
  CheckCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Attendee,
  RegistrationGroup,
  RefundHistoryItem,
  VoucherUsageHistory,
} from "@/modules/eventRegistration/features/EventWaitlist/api/getEventTransactionDetails/types";
import styles from "./RegistrationTransactionsTable.module.css";

interface RegistrationTransactionsTableProps {
  registrationGroups: RegistrationGroup[];
  attendees: Attendee[];
  expandedGroups: string[];
  isLoading: boolean;
  onToggleExpand: (groupId: string) => void;
  onRefundGroup: (groupId: string) => void;
  onRefundVouchers: (groupId: string, voucherCount: number) => void;
  onViewInvoice: (groupId: string) => void;
  getGroupAttendees: (groupId: string) => Attendee[];
  getGroupDisplayName: (group: RegistrationGroup) => string;
}

export default function RegistrationTransactionsTable({
  registrationGroups,
  attendees,
  expandedGroups,
  isLoading,
  onToggleExpand,
  onRefundGroup,
  onRefundVouchers,
  onViewInvoice,
  getGroupAttendees,
  getGroupDisplayName,
}: RegistrationTransactionsTableProps) {
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.loadingIcon} />
        <p className={styles.loadingText}>Loading transactions...</p>
      </div>
    );
  }

  if (registrationGroups.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Receipt className={styles.emptyStateIcon} />
        <p className={styles.emptyStateText}>
          No registration transactions found
        </p>
      </div>
    );
  }

  const getRefundStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return {
          className: styles.badgeSuccess,
          icon: Check,
          text: "Completed",
        };
      case "pending":
      case "processing":
        return { className: styles.badgePending, icon: Clock, text: "Pending" };
      case "failed":
        return { className: styles.badgeError, icon: X, text: "Failed" };
      default:
        return { className: styles.badgeDefault, icon: Info, text: status };
    }
  };

  const getVoucherStatusBadge = (isReturned: boolean) => {
    if (isReturned) {
      return {
        className: styles.badgeError,
        icon: RotateCcw,
        text: "Returned",
      };
    }
    return {
      className: styles.badgeSuccess,
      icon: CheckCircle,
      text: "Active",
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  };

  return (
    <div className={styles.container}>
      {registrationGroups.map((group) => {
        const isExpanded = expandedGroups.includes(group.id);
        const groupAttendees = getGroupAttendees(group.id);
        const formattedDate = group.createdAt
          ? format(new Date(group.createdAt), "MMM d, yyyy 'at' h:mm a")
          : "N/A";

        const canRefund =
          group.remainingRefundableAmount > 0 &&
          group.remainingRefundableSeats > 0;
        const hasRefunds =
          group.refundHistory && group.refundHistory.length > 0;
        const hasPendingRefunds = group.hasPendingRefunds;
        const hasVoucherHistory =
          group.voucherUsageHistory && group.voucherUsageHistory.length > 0;

        return (
          <div className={styles.card} key={group.id}>
            {/* Card Header - existing code remains the same */}
            <div
              className={styles.cardHeader}
              onClick={() => onToggleExpand(group.id)}
            >
              <div className={styles.headerTop}>
                <div className={styles.headerLeft}>
                  <div className={styles.companyIcon}>
                    <Building2 className={styles.companyIconSvg} />
                  </div>
                  <div className={styles.headerInfo}>
                    <h3 className={styles.companyName}>
                      {getGroupDisplayName(group)}
                    </h3>
                    <p className={styles.transactionDate}>{formattedDate}</p>
                  </div>
                </div>
                <div className={styles.headerRight}>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button className={styles.menuButton}>
                        <MoreHorizontal className={styles.menuIcon} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewInvoice(group.id);
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        View Invoice
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ChevronDown
                    className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ""}`}
                  />
                </div>
              </div>

              {/* Transaction Summary - existing code remains the same */}
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <Users className={styles.summaryIconSvg} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Attendees</span>
                    <span className={styles.summaryValue}>
                      {group.actualAttendeeCount || 0}
                    </span>
                  </div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <CreditCard className={styles.summaryIconSvg} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Paid Seats</span>
                    <span className={styles.summaryValue}>
                      {group.creditCardCount || 0}
                    </span>
                  </div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <Ticket className={styles.summaryIconSvg} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Vouchers</span>
                    <span className={styles.summaryValue}>
                      {group.voucherCount || 0}
                    </span>
                  </div>
                </div>

                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <DollarSign className={styles.summaryIconSvg} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Total Paid</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(group.paymentAmount || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Refund Status Bar - existing code remains the same */}
              {(hasRefunds || hasPendingRefunds) && (
                <div className={styles.refundStatusBar}>
                  <div className={styles.refundStatusInfo}>
                    <Info className={styles.refundStatusIcon} />
                    <span className={styles.refundStatusText}>
                      {hasPendingRefunds
                        ? `${group.refundHistory.filter((r) => r.status === "pending" || r.status === "processing").length} pending refund(s)`
                        : `${group.refundHistory.filter((r) => r.status === "completed").length} completed refund(s)`}
                    </span>
                  </div>
                  <div className={styles.refundAmounts}>
                    <span className={styles.refundedAmount}>
                      Refunded: {formatCurrency(group.refundedAmount)}
                    </span>
                    {group.pendingRefundAmount > 0 && (
                      <span className={styles.pendingAmount}>
                        Pending: {formatCurrency(group.pendingRefundAmount)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons - existing code remains the same */}
              <div className={styles.actionButtons}>
                <button
                  className={`${styles.actionButton} ${styles.voucherButton}`}
                  disabled={!group.voucherCount || group.voucherCount <= 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRefundVouchers(group.id, group.voucherCount || 0);
                  }}
                  title={
                    !group.voucherCount || group.voucherCount <= 0
                      ? "No vouchers available to return"
                      : "Return vouchers"
                  }
                >
                  <RotateCcw className={styles.buttonIcon} />
                  Return Vouchers
                </button>

                <button
                  className={`${styles.actionButton} ${styles.refundButton} ${!canRefund ? styles.buttonDisabled : ""}`}
                  disabled={!canRefund}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRefundGroup(group.id);
                  }}
                  title={
                    !canRefund
                      ? hasPendingRefunds
                        ? "All funds have been refunded or are pending refund"
                        : "All funds have been refunded"
                      : "Process refund"
                  }
                >
                  <RotateCcw className={styles.buttonIcon} />
                  Process Refund
                  {!canRefund && (
                    <AlertCircle className={styles.disabledIcon} />
                  )}
                </button>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className={styles.cardBody}>
                {/* Voucher Usage History Section - UPDATED DESIGN */}
                {hasVoucherHistory && (
                  <div className={styles.refundDetailsSection}>
                    <h4 className={styles.sectionTitle}>
                      <Ticket className={styles.sectionIcon} />
                      Voucher Usage & Returns
                    </h4>

                    <div className={styles.refundHistory}>
                      <h5 className={styles.historyTitle}>Voucher History</h5>
                      <div className={styles.historyList}>
                        {group.voucherUsageHistory.map((usage) => {
                          const badge = getVoucherStatusBadge(usage.isReturned);
                          const Icon = badge.icon;
                          return (
                            <div className={styles.historyItem} key={usage.id}>
                              <div className={styles.historyHeader}>
                                <div
                                  className={`${styles.badge} ${badge.className}`}
                                >
                                  <Icon className={styles.badgeIcon} />
                                  {badge.text}
                                </div>
                                <span className={styles.historyDate}>
                                  {format(
                                    new Date(usage.redeemedAt),
                                    "MMM d, yyyy",
                                  )}
                                </span>
                              </div>
                              <div className={styles.historyDetails}>
                                <div className={styles.historyRow}>
                                  <span className={styles.historyLabel}>
                                    Voucher:
                                  </span>
                                  <span className={styles.historyValue}>
                                    {usage.voucherName}
                                  </span>
                                </div>
                                {usage.attendeeName && (
                                  <div className={styles.historyRow}>
                                    <span className={styles.historyLabel}>
                                      Attendee:
                                    </span>
                                    <span className={styles.historyValue}>
                                      {usage.attendeeName}
                                      {usage.attendeeEmail && (
                                        <span
                                          className={styles.attendeeEmailInline}
                                        >
                                          {" "}
                                          ({usage.attendeeEmail})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                <div className={styles.historyRow}>
                                  <span className={styles.historyLabel}>
                                    Status:
                                  </span>
                                  <span className={styles.historyValue}>
                                    {usage.attendeeType === "enrolled"
                                      ? "Enrolled"
                                      : "Waitlisted"}
                                  </span>
                                </div>
                                {usage.isReturned && usage.returnReason && (
                                  <div className={styles.historyRow}>
                                    <span className={styles.historyLabel}>
                                      Reason:
                                    </span>
                                    <span className={styles.historyValue}>
                                      {usage.returnReason}
                                    </span>
                                  </div>
                                )}
                                {usage.isReturned && usage.returnedBy && (
                                  <div className={styles.historyRow}>
                                    <span className={styles.historyLabel}>
                                      By:
                                    </span>
                                    <span className={styles.historyValue}>
                                      {usage.returnedBy}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Refund Details Section - existing code remains the same */}
                {(hasRefunds || group.paymentAmount > 0) && (
                  <div className={styles.refundDetailsSection}>
                    <h4 className={styles.sectionTitle}>
                      <Receipt className={styles.sectionIcon} />
                      Payment & Refund Details
                    </h4>

                    <div className={styles.paymentSummary}>
                      <div className={styles.paymentRow}>
                        <span className={styles.paymentLabel}>
                          Original Payment
                        </span>
                        <span className={styles.paymentValue}>
                          {formatCurrency(group.totalPaid)}
                        </span>
                      </div>
                      <div className={styles.paymentRow}>
                        <span className={styles.paymentLabel}>
                          Total Refunded
                        </span>
                        <span className={styles.paymentValue}>
                          {formatCurrency(group.refundedAmount)}
                        </span>
                      </div>
                      {group.pendingRefundAmount > 0 && (
                        <div className={styles.paymentRow}>
                          <span className={styles.paymentLabel}>
                            Pending Refunds
                          </span>
                          <span
                            className={`${styles.paymentValue} ${styles.pendingText}`}
                          >
                            {formatCurrency(group.pendingRefundAmount)}
                          </span>
                        </div>
                      )}
                      <div
                        className={`${styles.paymentRow} ${styles.paymentRowTotal}`}
                      >
                        <span className={styles.paymentLabel}>
                          Remaining Balance
                        </span>
                        <span className={styles.paymentValueLarge}>
                          {formatCurrency(group.remainingRefundableAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Refund History - existing code remains the same */}
                    {hasRefunds && (
                      <div className={styles.refundHistory}>
                        <h5 className={styles.historyTitle}>Refund History</h5>
                        <div className={styles.historyList}>
                          {group.refundHistory.map((refund) => {
                            const badge = getRefundStatusBadge(refund.status);
                            const Icon = badge.icon;
                            return (
                              <div
                                className={styles.historyItem}
                                key={refund.id}
                              >
                                <div className={styles.historyHeader}>
                                  <div
                                    className={`${styles.badge} ${badge.className}`}
                                  >
                                    <Icon className={styles.badgeIcon} />
                                    {badge.text}
                                  </div>
                                  <span className={styles.historyDate}>
                                    {format(
                                      new Date(refund.createdAt),
                                      "MMM d, yyyy",
                                    )}
                                  </span>
                                </div>
                                <div className={styles.historyDetails}>
                                  <div className={styles.historyRow}>
                                    <span className={styles.historyLabel}>
                                      Amount:
                                    </span>
                                    <span className={styles.historyValue}>
                                      {formatCurrency(refund.requestedAmount)}
                                    </span>
                                  </div>
                                  {refund.seatsRefunded && (
                                    <div className={styles.historyRow}>
                                      <span className={styles.historyLabel}>
                                        Seats:
                                      </span>
                                      <span className={styles.historyValue}>
                                        {refund.seatsRefunded}
                                      </span>
                                    </div>
                                  )}
                                  <div className={styles.historyRow}>
                                    <span className={styles.historyLabel}>
                                      Reason:
                                    </span>
                                    <span className={styles.historyValue}>
                                      {refund.reason}
                                    </span>
                                  </div>
                                  {refund.requestedBy && (
                                    <div className={styles.historyRow}>
                                      <span className={styles.historyLabel}>
                                        By:
                                      </span>
                                      <span className={styles.historyValue}>
                                        {refund.requestedBy}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Attendees Section - existing code remains the same */}
                <div className={styles.attendeesSection}>
                  <h4 className={styles.sectionTitle}>
                    <Users className={styles.sectionIcon} />
                    Attendees ({groupAttendees.length})
                  </h4>

                  {groupAttendees.length > 0 ? (
                    <div className={styles.attendeesList}>
                      {groupAttendees.map((attendee) => (
                        <div
                          className={styles.attendeeCard}
                          key={attendee.registrationId}
                        >
                          <div className={styles.attendeeInfo}>
                            <p className={styles.attendeeName}>
                              {attendee.name}
                            </p>
                            <p className={styles.attendeeEmail}>
                              {attendee.email.toLowerCase()}
                            </p>
                          </div>
                          <div className={styles.attendeeStatus}>
                            <span
                              className={`${styles.statusBadge} ${
                                attendee.status === "registered"
                                  ? styles.statusRegistered
                                  : styles.statusPending
                              }`}
                            >
                              {attendee.status === "registered"
                                ? "Registered"
                                : "Pending"}
                            </span>
                            {attendee.isRefunded && (
                              <span className={styles.refundedBadge}>
                                <RotateCcw className={styles.refundedIcon} />
                                Refunded
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noAttendees}>
                      <Users className={styles.noAttendeesIcon} />
                      <p>No attendees found in this group</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
